import { Box, Button } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import EmptyState from "src/grading/components/EmptyState/EmptyState";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import Loader from "src/grading/components/Loader/Loader";
import NoAccess from "src/grading/components/NoAccess/NoAccess";
import { QueryStatus, queryState } from "src/grading/utils/queryState";

/** The parts of an RTK Query hook result that QueryStateView reads; any query hook result fits. */
export interface QueryStateViewQuery extends QueryStatus {
  data?: unknown;
  isFetching?: boolean;
  refetch?: () => unknown;
}

export interface QueryStateViewProps {
  /** The page's primary query result, e.g. `useGetDistrictQuery(id)`. */
  query: QueryStateViewQuery;
  /** Shown on a 404 instead of the default "This page isn't available" message. */
  notFound?: ReactNode;
  /** Error text above the Retry button; defaults to "This page couldn't load…". */
  errorMessage?: string;
  /**
   * Heading level of the no-access title; 1 by default, since it replaces the page. The default
   * not-found title uses the same level, but at least 2 (EmptyState's lowest).
   */
  headingLevel?: 1 | 2 | 3;
  /** The loaded page. A function is only called after the query has loaded, so it may read `data!` (unless the query is skipped). */
  children: ReactNode | (() => ReactNode);
}

/**
 * Renders a page's loading, no-access (403), not-found (404) and error-with-Retry states for its
 * primary query, and the page itself once the query has loaded (FR-048). A failed load is never
 * rendered as the page, so it can't look empty.
 *
 * ```tsx
 * const district = useGetDistrictQuery(districtId);
 * return (
 *   <QueryStateView query={district}>
 *     {() => <DistrictForm district={district.data!} />}
 *   </QueryStateView>
 * );
 * ```
 *
 * When a background refetch fails after data has loaded (for example while polling), the page stays
 * on screen under an error banner with Retry, so open forms and dialogs are not lost.
 */
export const QueryStateView = ({
  query,
  notFound,
  errorMessage,
  headingLevel = 1,
  children,
}: QueryStateViewProps) => {
  const { t } = useTranslation();
  const state = queryState(query);
  const render = () =>
    typeof children === "function" ? (children as () => ReactNode)() : children;

  const retryAction = query.refetch ? (
    <Button
      color="inherit"
      size="small"
      disabled={query.isFetching}
      onClick={() => query.refetch?.()}
    >
      {t("common.retry")}
    </Button>
  ) : undefined;

  switch (state) {
    case "loading":
      return <Loader />;
    case "no-access":
      return <NoAccess headingLevel={headingLevel} />;
    case "not-found":
      return (
        <Box data-testid="not-found">
          {notFound ?? (
            <EmptyState
              title={t("queryState.notFoundTitle")}
              body={t("queryState.notFoundBody")}
              headingLevel={headingLevel === 1 ? 2 : headingLevel}
            />
          )}
        </Box>
      );
    case "error":
      if (query.data !== undefined)
        return (
          <>
            <Box sx={{ mb: 2 }}>
              <InlineAlert severity="error" action={retryAction}>
                {errorMessage ?? t("queryState.staleError")}
              </InlineAlert>
            </Box>
            {render()}
          </>
        );
      return (
        <Box data-testid="load-error">
          <InlineAlert severity="error" action={retryAction}>
            {errorMessage ?? t("queryState.error")}
          </InlineAlert>
        </Box>
      );
    default:
      return <>{render()}</>;
  }
};

export default QueryStateView;

export interface QueryErrorAlertProps {
  /** The secondary query that failed; its `refetch` backs the Retry button. */
  query: Pick<QueryStateViewQuery, "isFetching" | "refetch">;
  /** What could not load, e.g. "The schools couldn't load." */
  children: ReactNode;
  /** Stable id for the e2e suite, on the wrapper. */
  "data-testid"?: string;
}

/**
 * The inline error for a secondary query (a section, a status line, a search), with Retry (FR-048).
 * The rest of the page stays on screen; only the failed part says it couldn't load, so the failure
 * never reads as "nothing here".
 */
export const QueryErrorAlert = ({
  query,
  children,
  "data-testid": testId,
}: QueryErrorAlertProps) => {
  const { t } = useTranslation();
  return (
    <Box data-testid={testId}>
      <InlineAlert
        severity="error"
        action={
          query.refetch ? (
            <Button
              color="inherit"
              size="small"
              disabled={query.isFetching}
              onClick={() => query.refetch?.()}
            >
              {t("common.retry")}
            </Button>
          ) : undefined
        }
      >
        {children}
      </InlineAlert>
    </Box>
  );
};
